// src/contexts/FlowAutomationContext.js
import React, { createContext, useContext, useState, useCallback } from 'react';
import flowAutomationService from '../services/flowAutomation.service';

const FlowAutomationContext = createContext();

export const useFlowAutomation = () => {
    const context = useContext(FlowAutomationContext);
    if (!context) {
        throw new Error('useFlowAutomation must be used within FlowAutomationProvider');
    }
    return context;
};

// Generate unique IDs for steps and connections
let idCounter = 0;
export const generateId = (prefix = 'node') => `${prefix}_${Date.now()}_${++idCounter}`;

export const FlowAutomationProvider = ({ children }) => {
    // ─── List State ──────────────────────────────────────────────
    const [workflows, setWorkflows] = useState([]);
    const [executions, setExecutions] = useState([]);
    const [templates, setTemplates] = useState(() => flowAutomationService.getBuiltInTemplates());
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({ type: '', status: '', search: '' });

    // ─── Builder State (current workflow being edited) ───────────
    const [currentWorkflow, setCurrentWorkflow] = useState(null);
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [selectedConnectionId, setSelectedConnectionId] = useState(null);
    const [isDirty, setIsDirty] = useState(false);

    // ─── Data Fetching ───────────────────────────────────────────

    const fetchWorkflows = useCallback(async (filterOverrides) => {
        setLoading(true);
        setError(null);
        try {
            const result = await flowAutomationService.getWorkflows(filterOverrides || filters);
            if (result.success !== false) {
                setWorkflows(result.workflows || []);
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const fetchWorkflow = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const result = await flowAutomationService.getWorkflow(id);
            if (result.success !== false) {
                setCurrentWorkflow(result.workflow);
                setIsDirty(false);
                return result.workflow;
            } else {
                setError(result.error);
                return null;
            }
        } catch (err) {
            setError(err.message);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchExecutions = useCallback(async (workflowId, executionFilters) => {
        setLoading(true);
        try {
            const result = await flowAutomationService.getExecutions(workflowId, executionFilters);
            if (result.success !== false) {
                setExecutions(result.executions || []);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchTemplates = useCallback(async (category) => {
        try {
            const result = await flowAutomationService.getTemplates(category);
            const tpls = result.templates || [];
            setTemplates(tpls);
            return tpls;
        } catch (err) {
            // Fallback: load built-in templates directly
            const builtIn = flowAutomationService.getBuiltInTemplates(category);
            setTemplates(builtIn);
            return builtIn;
        }
    }, []);

    const fetchStats = useCallback(async () => {
        try {
            const result = await flowAutomationService.getAutomationStats();
            setStats(result.stats || null);
        } catch (err) {
            setError(err.message);
        }
    }, []);

    // ─── Workflow CRUD Actions ───────────────────────────────────

    const saveWorkflow = useCallback(async (workflowData) => {
        const data = workflowData || currentWorkflow;
        if (!data) return null;

        // Sync top-level trigger from the trigger step config before saving
        const triggerStep = data.steps?.find(s => s.type === 'trigger');
        if (triggerStep?.config) {
            data.trigger = triggerStep.config;
        }

        setLoading(true);
        try {
            let result;
            if (data._isNew) {
                const { _isNew, ...rest } = data;
                result = await flowAutomationService.createWorkflow(rest);
            } else {
                result = await flowAutomationService.updateWorkflow(data.id, data);
            }

            if (result.success !== false) {
                setCurrentWorkflow(result.workflow);
                setIsDirty(false);
                await fetchWorkflows();
            }
            return result;
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    }, [currentWorkflow, fetchWorkflows]);

    const deleteWorkflow = useCallback(async (id) => {
        try {
            const result = await flowAutomationService.deleteWorkflow(id);
            if (result.success !== false) {
                setWorkflows(prev => prev.filter(w => w.id !== id));
                if (currentWorkflow?.id === id) {
                    setCurrentWorkflow(null);
                }
            }
            return result;
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, [currentWorkflow]);

    const duplicateWorkflow = useCallback(async (id) => {
        try {
            const result = await flowAutomationService.duplicateWorkflow(id);
            if (result.success !== false) {
                await fetchWorkflows();
            }
            return result;
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, [fetchWorkflows]);

    const toggleStatus = useCallback(async (id, status) => {
        try {
            const result = await flowAutomationService.toggleWorkflowStatus(id, status);
            if (result.success !== false) {
                setWorkflows(prev => prev.map(w => w.id === id ? { ...w, status } : w));
                if (currentWorkflow?.id === id) {
                    setCurrentWorkflow(prev => ({ ...prev, status }));
                }
            }
            return result;
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, [currentWorkflow]);

    // ─── Canvas Editing Actions ──────────────────────────────────

    const initNewWorkflow = useCallback((overrides = {}) => {
        const newWorkflow = {
            id: generateId('wf'),
            name: 'Untitled Workflow',
            description: '',
            type: 'custom',
            status: 'draft',
            scope: 'global',
            trigger: { event: 'manual', filters: {} },
            steps: [
                {
                    id: generateId('step'),
                    type: 'trigger',
                    label: 'Trigger',
                    config: { event: 'manual', filters: {} },
                    position: { x: 300, y: 80 },
                },
            ],
            connections: [],
            version: 1,
            sla_hours: null,
            escalation_rules: [],
            template_id: null,
            _isNew: true,
            ...overrides,
        };
        setCurrentWorkflow(newWorkflow);
        setSelectedNodeId(null);
        setSelectedConnectionId(null);
        setIsDirty(false);
        return newWorkflow;
    }, []);

    const addStep = useCallback((step) => {
        setCurrentWorkflow(prev => {
            if (!prev) return prev;
            return { ...prev, steps: [...prev.steps, step] };
        });
        setIsDirty(true);
    }, []);

    const updateStep = useCallback((stepId, updates) => {
        setCurrentWorkflow(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                steps: prev.steps.map(s => s.id === stepId ? { ...s, ...updates } : s),
            };
        });
        setIsDirty(true);
    }, []);

    const removeStep = useCallback((stepId) => {
        setCurrentWorkflow(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                steps: prev.steps.filter(s => s.id !== stepId),
                connections: prev.connections.filter(
                    c => c.from_step_id !== stepId && c.to_step_id !== stepId
                ),
            };
        });
        if (selectedNodeId === stepId) setSelectedNodeId(null);
        setIsDirty(true);
    }, [selectedNodeId]);

    const updateStepPosition = useCallback((stepId, position) => {
        setCurrentWorkflow(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                steps: prev.steps.map(s =>
                    s.id === stepId ? { ...s, position } : s
                ),
            };
        });
        setIsDirty(true);
    }, []);

    const addConnection = useCallback((connection) => {
        setCurrentWorkflow(prev => {
            if (!prev) return prev;
            // Prevent duplicate connections
            const exists = prev.connections.some(
                c => c.from_step_id === connection.from_step_id && c.to_step_id === connection.to_step_id
            );
            if (exists) return prev;
            return { ...prev, connections: [...prev.connections, connection] };
        });
        setIsDirty(true);
    }, []);

    const removeConnection = useCallback((connectionId) => {
        setCurrentWorkflow(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                connections: prev.connections.filter(c => c.id !== connectionId),
            };
        });
        if (selectedConnectionId === connectionId) setSelectedConnectionId(null);
        setIsDirty(true);
    }, [selectedConnectionId]);

    const updateWorkflowField = useCallback((field, value) => {
        setCurrentWorkflow(prev => {
            if (!prev) return prev;
            return { ...prev, [field]: value };
        });
        setIsDirty(true);
    }, []);

    // ─── Draft Auto-save ─────────────────────────────────────────

    const saveDraft = useCallback(() => {
        if (currentWorkflow && isDirty) {
            flowAutomationService.saveDraftToLocal(currentWorkflow);
        }
    }, [currentWorkflow, isDirty]);

    // ─── Computed Values ─────────────────────────────────────────

    const selectedNode = currentWorkflow?.steps?.find(s => s.id === selectedNodeId) || null;
    const selectedConnection = currentWorkflow?.connections?.find(c => c.id === selectedConnectionId) || null;

    const value = {
        // List state
        workflows,
        executions,
        templates,
        stats,
        loading,
        error,
        filters,
        setFilters,

        // Builder state
        currentWorkflow,
        setCurrentWorkflow,
        selectedNodeId,
        setSelectedNodeId,
        selectedConnectionId,
        setSelectedConnectionId,
        selectedNode,
        selectedConnection,
        isDirty,

        // Data fetching
        fetchWorkflows,
        fetchWorkflow,
        fetchExecutions,
        fetchTemplates,
        fetchStats,

        // Workflow CRUD
        saveWorkflow,
        deleteWorkflow,
        duplicateWorkflow,
        toggleStatus,

        // Canvas editing
        initNewWorkflow,
        addStep,
        updateStep,
        removeStep,
        updateStepPosition,
        addConnection,
        removeConnection,
        updateWorkflowField,
        saveDraft,
    };

    return (
        <FlowAutomationContext.Provider value={value}>
            {children}
        </FlowAutomationContext.Provider>
    );
};

export default FlowAutomationContext;
